/**
 * Audio analysis data passed to visualizers
 */
export interface AudioAnalysisData {
  beat: number;      // 0-1: Immediate beat detection
  bar: number;       // 0-1: Bar/measure detection
  energy: number;    // 0-1: Overall energy level
  loudness: number;  // 0-1: Current loudness
  pitches: number[]; // Array of 12 pitch values (0-1)
}

/**
 * Abstract base class for audio visualizers
 * Provides common functionality and interface that all visualizers must implement
 */
export abstract class BaseVisualizer {
  protected canvas: HTMLCanvasElement;
  protected ctx: CanvasRenderingContext2D;
  protected animationId: number | null = null;
  protected simulatedData: Uint8Array;
  protected isPaused: boolean = false;
  
  // Current audio analysis data
  protected currentBeat: number = 0;
  protected currentBar: number = 0;
  protected currentEnergy: number = 0.5;
  protected currentLoudness: number = 0.7;
  protected currentPitches: number[] = [];
  protected hasRealData: boolean = false;
  
  // Time tracking for animations
  protected startTime: number = 0;
  protected currentTime: number = 0;
  protected pausedTime: number = 0;

  constructor(canvas: HTMLCanvasElement, dataSize: number = 180) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D context from canvas');
    }
    this.ctx = ctx;
    this.simulatedData = new Uint8Array(dataSize);
    
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  /**
   * Resize handler - updates canvas dimensions
   */
  protected resize(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  /**
   * Update with real-time audio analysis data
   */
  updateAnalysisData(data: AudioAnalysisData): void {
    this.hasRealData = true;
    
    this.currentBeat = data.beat || 0;
    this.currentBar = data.bar || 0;
    this.currentEnergy = data.energy || 0.5;
    this.currentLoudness = data.loudness || 0.7;
    this.currentPitches = data.pitches || [];

    this.generateSimulatedData();
  }

  /**
   * Generate simulated frequency data based on audio analysis
   * Override this to customize how frequency data is generated
   */
  protected generateSimulatedData(): void {
    const time = this.currentTime;
    
    const baseLevel = Math.max(0.5, this.currentEnergy) * 80;
    const beatBoost = this.currentBeat * 150;
    const barBoost = this.currentBar * 80;

    for (let i = 0; i < this.simulatedData.length; i++) {
      const angle = (i / this.simulatedData.length) * Math.PI * 2;
      
      let pitchContribution = 0;
      if (this.currentPitches.length > 0) {
        const pitchIndex = Math.floor((i / this.simulatedData.length) * this.currentPitches.length);
        pitchContribution = this.currentPitches[pitchIndex] * 80;
      } else {
        pitchContribution = Math.abs(Math.sin(time * 2 + angle * 3)) * 60;
      }

      const bassBoost = Math.max(0.3, 1 - (i / this.simulatedData.length) * 0.6);
      const circularPattern = Math.abs(Math.sin(angle * 2 + time * 0.5)) * 0.4 + 0.6;
      
      let value = baseLevel * bassBoost * circularPattern;
      value += pitchContribution * 0.8;
      value += beatBoost * (1 - i / this.simulatedData.length);
      value += barBoost * circularPattern;
      value += Math.sin(angle * 6 + time) * 20 * this.currentEnergy;
      value *= Math.max(0.6, this.currentLoudness);
      
      this.simulatedData[i] = Math.min(255, Math.max(30, value));
    }
  }

  /**
   * Start the visualization animation loop
   */
  start(): void {
    if (this.animationId) return;
    
    this.startTime = Date.now() / 1000;
    
    const draw = () => {
      this.animationId = requestAnimationFrame(draw);
      
      // Don't update time or render if paused
      if (!this.isPaused) {
        this.currentTime = Date.now() / 1000 - this.startTime;
        this.generateSimulatedData();
        this.render();
      }
    };

    draw();
  }
  
  /**
   * Trigger intro animation (can be overridden by subclasses)
   * Default implementation does nothing
   */
  public startIntro(): void {
    // Override in subclass to implement intro animation
  }

  /**
   * Pause the visualization (keeps animation loop but stops updates)
   */
  pause(): void {
    this.isPaused = true;
    this.pausedTime = this.currentTime;
  }

  /**
   * Resume the visualization
   */
  resume(): void {
    this.isPaused = false;
    // Adjust start time to account for pause duration
    const pauseDuration = (Date.now() / 1000 - this.startTime) - this.pausedTime;
    this.startTime += pauseDuration;
  }

  /**
   * Stop the visualization
   */
  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stop();
  }

  /**
   * Abstract render method - must be implemented by subclasses
   * This is where the actual visualization drawing happens
   */
  protected abstract render(): void;

  /**
   * Utility: Get center point of canvas
   */
  protected getCenter(): { x: number; y: number } {
    return {
      x: this.canvas.width / 2,
      y: this.canvas.height / 2
    };
  }

  /**
   * Utility: Get base radius for circular visualizations
   */
  protected getBaseRadius(): number {
    const { x, y } = this.getCenter();
    return Math.min(x, y) * 0.6;
  }

  /**
   * Utility: Create HSL color string
   */
  protected hsl(h: number, s: number, l: number, a: number = 1): string {
    if (a < 1) {
      return `hsla(${h}, ${s}%, ${l}%, ${a})`;
    }
    return `hsl(${h}, ${s}%, ${l}%)`;
  }

  /**
   * Utility: Create RGB color string
   */
  protected rgb(r: number, g: number, b: number, a: number = 1): string {
    if (a < 1) {
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
  }
}
